import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, Brain, RotateCcw, HelpCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error2.mp3';

interface MysteryBagGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface BagItem {
  color: string;
  count: number;
}

interface DrawResult {
  color: string;
  drawn: number;
}

const MysteryBagGame: React.FC<MysteryBagGameProps> = ({ mode, onReset, language }) => {
  const [level, setLevel] = useState(1);
  const [gameMode, setGameMode] = useState<'prediction' | 'simulation'>('prediction');
  const [bagContents, setBagContents] = useState<BagItem[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<string>('');
  const [userPrediction, setUserPrediction] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [drawResults, setDrawResults] = useState<DrawResult[]>([]);
  const [totalDraws, setTotalDraws] = useState(0);
  const [score, setScore] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });

  const colors = {
    1: ['red', 'blue'],
    2: ['red', 'blue', 'green'],
    3: ['red', 'blue', 'green', 'yellow']
  };

  const colorNames = {
    red: language === 'en' ? 'red' : 'rouge',
    blue: language === 'en' ? 'blue' : 'bleu',
    green: language === 'en' ? 'green' : 'vert',
    yellow: language === 'en' ? 'yellow' : 'jaune'
  };

  const generateBagContents = () => {
    const levelColors = colors[level as keyof typeof colors];
    const contents: BagItem[] = [];
    
    levelColors.forEach(color => {
      const count = Math.floor(Math.random() * 8) + 2; // 2-9 items
      contents.push({ color, count });
    });
    
    setBagContents(contents);
    setDrawResults(contents.map(item => ({ color: item.color, drawn: 0 })));
    setTotalDraws(0);
    generateQuestion(contents);
  };

  const generateQuestion = (contents: BagItem[]) => {
    const total = contents.reduce((sum, item) => sum + item.count, 0);
    const questionTypes = ['probability', 'expected', 'comparison', 'fraction'];
    const type = questionTypes[Math.floor(Math.random() * questionTypes.length)];

    let question = '';
    let answer = '';

    switch (type) {
      case 'probability':
        const randomColor = contents[Math.floor(Math.random() * contents.length)];
        const probability = Math.round((randomColor.count / total) * 100);
        question = language === 'en'
          ? `What is the probability of drawing a ${colorNames[randomColor.color as keyof typeof colorNames]} ball? (in %)`
          : `Quelle est la probabilité de tirer une bille ${colorNames[randomColor.color as keyof typeof colorNames]} ? (en %)`;
        answer = `${probability}%`;
        break;

      case 'expected':
        const draws = [10, 20, 50][Math.floor(Math.random() * 3)];
        const targetColor = contents[Math.floor(Math.random() * contents.length)];
        const expected = Math.round((targetColor.count / total) * draws);
        question = language === 'en'
          ? `If you draw ${draws} balls, how many ${colorNames[targetColor.color as keyof typeof colorNames]} balls do you expect?`
          : `Si vous tirez ${draws} billes, combien de billes ${colorNames[targetColor.color as keyof typeof colorNames]} attendez-vous ?`;
        answer = expected.toString();
        break;

      case 'comparison':
        const sortedContents = [...contents].sort((a, b) => b.count - a.count);
        question = language === 'en'
          ? 'Which color is most likely to be drawn?'
          : 'Quelle couleur a le plus de chances d\'être tirée ?';
        answer = colorNames[sortedContents[0].color as keyof typeof colorNames];
        break;

      case 'fraction':
        const fractionColor = contents[Math.floor(Math.random() * contents.length)];
        const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
        const divisor = gcd(fractionColor.count, total);
        const numerator = fractionColor.count / divisor;
        const denominator = total / divisor;
        question = language === 'en'
          ? `Express the probability of drawing ${colorNames[fractionColor.color as keyof typeof colorNames]} as a fraction:`
          : `Exprimez la probabilité de tirer ${colorNames[fractionColor.color as keyof typeof colorNames]} sous forme de fraction :`;
        answer = `${numerator}/${denominator}`;
        break;
    }

    setCurrentQuestion(question);
    setCorrectAnswer(answer);
  };

  const handlePrediction = () => {
    if (!userPrediction.trim()) return;

    setShowAnswer(true);
    const correct = userPrediction.toLowerCase().trim() === correctAnswer.toLowerCase().trim();
    
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);

    if (correct) {
      setScore(score + 1);
      playSuccess();
    } else {
      playError();
    }

    setTimeout(() => {
      setQuestionCount(questionCount + 1);
      if (questionCount >= 9) {
        saveProgress(newStats);
      } else {
        generateBagContents();
        setUserPrediction('');
        setShowAnswer(false);
      }
    }, 2000);
  };

  const simulateDraw = () => {
    if (isDrawing) return;
    
    setIsDrawing(true);
    const total = bagContents.reduce((sum, item) => sum + item.count, 0);
    const random = Math.random() * total;
    
    let cumulative = 0;
    let drawnColor = '';
    
    for (const item of bagContents) {
      cumulative += item.count;
      if (random < cumulative) {
        drawnColor = item.color;
        break;
      }
    }

    setTimeout(() => {
      setDrawResults(prev => prev.map(result =>
        result.color === drawnColor
          ? { ...result, drawn: result.drawn + 1 }
          : result
      ));
      setTotalDraws(prev => prev + 1);
      setIsDrawing(false);
    }, 1000);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'probability',
        mode: 'bag',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setLevel(1);
    setScore(0);
    setQuestionCount(0);
    setUserPrediction('');
    setShowAnswer(false);
    setTotalDraws(0);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
    generateBagContents();
  };

  useEffect(() => {
    generateBagContents();
  }, [level]);

  const getColorClass = (color: string) => {
    const classes = {
      red: 'bg-red-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500'
    };
    return classes[color as keyof typeof classes];
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <select
            value={level}
            onChange={(e) => setLevel(parseInt(e.target.value))}
            className="px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg"
          >
            <option value={1}>{language === 'en' ? 'Level 1 (2 colors)' : 'Niveau 1 (2 couleurs)'}</option>
            <option value={2}>{language === 'en' ? 'Level 2 (3 colors)' : 'Niveau 2 (3 couleurs)'}</option>
            <option value={3}>{language === 'en' ? 'Level 3 (4 colors)' : 'Niveau 3 (4 couleurs)'}</option>
          </select>
          
          <button
            onClick={() => setGameMode(gameMode === 'prediction' ? 'simulation' : 'prediction')}
            className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
          >
            {gameMode === 'prediction' 
              ? (language === 'en' ? 'Simulation Mode' : 'Mode Simulation')
              : (language === 'en' ? 'Prediction Mode' : 'Mode Prédiction')}
          </button>
          
          <button
            onClick={resetGame}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            <RotateCcw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {questionCount >= 10 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Brain className="h-16 w-16 text-indigo-600 dark:text-indigo-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Exercise Complete!' : 'Exercice terminé !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{score}/10</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Score' : 'Score'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                {Math.round((score / 10) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sac mystérieux */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-6 text-center">
              {language === 'en' ? 'Mystery Bag' : 'Sac Mystérieux'}
            </h2>
            
            {/* Contenu du sac */}
            <div className="bg-amber-100 dark:bg-amber-900/30 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-amber-800 dark:text-amber-200 mb-3">
                {language === 'en' ? 'Bag Contents:' : 'Contenu du sac :'}
              </h3>
              <div className="space-y-2">
                {bagContents.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full ${getColorClass(item.color)}`}></div>
                      <span className="text-amber-700 dark:text-amber-300">
                        {colorNames[item.color as keyof typeof colorNames]}
                      </span>
                    </div>
                    <span className="font-medium text-amber-800 dark:text-amber-200">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700 dark:text-amber-300">
                    {language === 'en' ? 'Total:' : 'Total :'}
                  </span>
                  <span className="font-medium text-amber-800 dark:text-amber-200">
                    {bagContents.reduce((sum, item) => sum + item.count, 0)}
                  </span>
                </div>
              </div>
            </div>

            {gameMode === 'simulation' && (
              <div className="text-center">
                <button
                  onClick={simulateDraw}
                  disabled={isDrawing}
                  className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50"
                >
                  {isDrawing 
                    ? (language === 'en' ? 'Drawing...' : 'Tirage...')
                    : (language === 'en' ? 'Draw a Ball' : 'Tirer une bille')}
                </button>
              </div>
            )}
          </div>

          {/* Questions et résultats */}
          <div className="space-y-6">
            {gameMode === 'prediction' ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <HelpCircle className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                    {language === 'en' ? 'Question' : 'Question'} {questionCount + 1}/10
                  </h3>
                </div>
                
                <p className="text-slate-700 dark:text-slate-300 mb-4">
                  {currentQuestion}
                </p>

                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={userPrediction}
                    onChange={(e) => setUserPrediction(e.target.value)}
                    className="flex-1 p-3 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    placeholder={language === 'en' ? 'Your answer' : 'Votre réponse'}
                    disabled={showAnswer}
                  />
                  <button
                    onClick={handlePrediction}
                    disabled={!userPrediction.trim() || showAnswer}
                    className="px-4 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50"
                  >
                    {language === 'en' ? 'Submit' : 'Valider'}
                  </button>
                </div>

                {showAnswer && (
                  <div className={`p-3 rounded-lg ${
                    userPrediction.toLowerCase().trim() === correctAnswer.toLowerCase().trim()
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                  }`}>
                    <p className="font-medium">
                      {userPrediction.toLowerCase().trim() === correctAnswer.toLowerCase().trim()
                        ? (language === 'en' ? 'Correct!' : 'Correct !')
                        : (language === 'en' ? `Incorrect. The answer was: ${correctAnswer}` : `Incorrect. La réponse était : ${correctAnswer}`)}
                    </p>
                  </div>
                )}

                <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                  {language === 'en' ? 'Score:' : 'Score :'} {score}/{questionCount + 1}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                  {language === 'en' ? 'Draw Results' : 'Résultats des tirages'}
                </h3>
                
                <div className="space-y-3">
                  {drawResults.map((result, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${getColorClass(result.color)}`}></div>
                        <span className="text-slate-700 dark:text-slate-300">
                          {colorNames[result.color as keyof typeof colorNames]}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          {result.drawn}
                        </span>
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          {totalDraws > 0 ? Math.round((result.drawn / totalDraws) * 100) : 0}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">
                      {language === 'en' ? 'Total draws:' : 'Tirages totaux :'}
                    </span>
                    <span className="font-medium text-slate-800 dark:text-slate-100">
                      {totalDraws}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Aide */}
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                {language === 'en' ? 'Help:' : 'Aide :'}
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• {language === 'en' ? 'Probability = (favorable outcomes) / (total outcomes)' : 'Probabilité = (résultats favorables) / (résultats totaux)'}</li>
                <li>• {language === 'en' ? 'Express as percentage: multiply by 100' : 'Exprimer en pourcentage : multiplier par 100'}</li>
                <li>• {language === 'en' ? 'Expected value = probability × number of trials' : 'Valeur attendue = probabilité × nombre d\'essais'}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MysteryBagGame;